import React, {useCallback, useContext, useEffect, useState} from 'react';
import UserContext from '../common/UserContext.js';

// css
import '../../assets/css/Users.css';

// components
import {
    ColumnMenu,
    LongDataNoWrapCell,
    NoWrapCell,
} from '../common/Grid.js';
import UserConfigHamburger from './UserConfigHamburger.js';
import AddUserModal from './AddUserModal.js';
import EditUserModal from './EditUserModal.js';
import RemoveUserModal from './RemoveUserModal.js';

// kendo react
import {Grid, GridColumn, GridNoRecords} from '@progress/kendo-react-grid';
import {Button} from '@progress/kendo-react-buttons';
import {process} from '@progress/kendo-data-query';

// multilingual
import {useLocalization} from '@progress/kendo-react-intl';
import {
    addUserKey,
    aliasKey,
    nameKey,
    emailKey,
    hostKey,
    departmentOrgKey,
    statusKey,
    noDataAvailableKey,
    mainMessages,
} from '../../assets/text/MultilingualText.js';
import {useSupportContext} from "../common/SupportContext";

// consts for ea users table
const initialGridState = {
    skip: 0,
    take: 20
};

function UsersGrid(props) {
    const {
        users,
        getUsers,
        setIsLoading
    } = props;
    const {siteLanguageDefault} = useContext(UserContext);
    const {dispatch} = useSupportContext();
    const localization = useLocalization();

    const [user, setUser] = useState("");

    const [userGridState, setUserGridState] = useState(initialGridState);
    const [userState, setUserState] = useState(
        process(
            users.map((user) => ({...user})),
            initialGridState
        )
    );

    const [isVisible, setIsVisible] = useState({
        addUser: false,
        editUser: false,
        removeUser: false
    });

    const defaultGridMessage = localization.toLanguageString(
        noDataAvailableKey,
        mainMessages[siteLanguageDefault][noDataAvailableKey]
    );
    const [gridNoRecordsMessage] = useState(defaultGridMessage);

    const formatHosts = (hosts) => {
        if (Array.isArray(hosts)) {
            return hosts.filter(Boolean).join(' | ');
        }

        if (typeof hosts === 'string') {
            return hosts
                .split('|')
                .map((host) => host.trim())
                .filter(Boolean)
                .join(' | ');
        }

        return '';
    };

    /*
     * onUserStateChange(event) sets the user grid state and user state on grid's state change
     * @param {event} the grid selection change event
    */
    const onUserStateChange = useCallback((event) => {
        const sort = event?.dataState?.sort || [];
        const newUserState = process(
            users.map((user) => ({...user})),
            event.dataState
        );
        setUserGridState(event.dataState);
        setUserState(newUserState);
        // support metadata filter and page size
        dispatch({ type: 'UPDATE_TRANSACTION', payload: {filters: event?.dataState?.filter, sort: sort[0], page_size: event?.dataState?.take}});
    }, [users]); // eslint-disable-line react-hooks/exhaustive-deps

    /*
    * showEditUser() opens the edit user modal
    * @param {userData} an object containing the row's user data
    */
    const showEditUser = (userData) => {
        // support metadata push
        dispatch({ type: 'UPDATE_TRANSACTION', payload: {phu_id: userData?.phu_id}});

        userData.phu_id ? setUser(userData) : setUser("");
        setIsVisible(isVisible => ({...isVisible, editUser: true}));
    }

    const showAddUser = () => {
        setUser({});
        setIsVisible((isVisible) => ({...isVisible, addUser: true}));
    }

    /*
    * showRemoveUser() opens the remove user modal
    * @param {userData} an object containing the row's user data
    */
    const showRemoveUser = (userData) => {
        // support metadata push
        dispatch({ type: 'UPDATE_TRANSACTION', payload: {phu_id: userData?.phu_id}});

        userData.phu_id ? setUser(userData) : setUser("");
        setIsVisible(isVisible => ({...isVisible, removeUser: true}));
    }

    // user config grid hamburger menu cell
    const UserConfigHamburgerCell = (props) => {
        return <UserConfigHamburger
            showEditUser={showEditUser}
            showRemoveUser={showRemoveUser}
            userData={props.dataItem}
            {...props}
        />
    }

    // initialize ea users table
    useEffect(() => {
        if (users.length && userGridState) {
            setUserState(process(users, userGridState));
        } else {
            setUserState(process(users, initialGridState));
        }
    }, [users]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <div
                className={'k-mb-2'}
                style={{
                    display: 'flex',
                }}
            >
                <Button
                    themeColor={"primary"}
                    size={"large"}
                    fillMode={"solid"}
                    type={"button"}
                    onClick={showAddUser}
                >
                    {localization.toLanguageString(addUserKey, mainMessages[siteLanguageDefault][addUserKey])}
                </Button>
            </div>
            <Grid
                className={"users-grid"}
                data={userState}
                sortable={true}
                scrollable={"none"}
                pageable={(users.length <= 20) ? false : {
                    buttonCount: 5,
                    pageSizes: [10, 20, 50, 100],
                }}
                total={userState.total}
                onDataStateChange={onUserStateChange}
                {...userGridState}
            >
                <GridNoRecords>
                    {gridNoRecordsMessage}
                </GridNoRecords>
                <GridColumn
                    cell={UserConfigHamburgerCell}
                    width={"1.25rem"}
                />
                <GridColumn
                    field="alias"
                    title={localization.toLanguageString(aliasKey, mainMessages[siteLanguageDefault][aliasKey])}
                    columnMenu={ColumnMenu}
                    cell={NoWrapCell}
                />
                <GridColumn
                    field="name"
                    title={localization.toLanguageString(nameKey, mainMessages[siteLanguageDefault][nameKey])}
                    columnMenu={ColumnMenu}
                    cell={NoWrapCell}
                />
                <GridColumn
                    field="email"
                    title={localization.toLanguageString(emailKey, mainMessages[siteLanguageDefault][emailKey])}
                    columnMenu={ColumnMenu}
                    cell={NoWrapCell}
                />
                <GridColumn
                    field="host_id"
                    title={localization.toLanguageString(hostKey, mainMessages[siteLanguageDefault][hostKey])}
                    columnMenu={ColumnMenu}
                    cell={(props) => (
                        <td>
                            <div className={'text-nowrap'}>
                                {formatHosts(props.dataItem.host_id)}
                            </div>
                        </td>
                    )}
                />
                <GridColumn
                    field="department"
                    title={localization.toLanguageString(departmentOrgKey, mainMessages[siteLanguageDefault][departmentOrgKey])}
                    columnMenu={ColumnMenu}
                    cell={LongDataNoWrapCell}
                />
                <GridColumn
                    field="status"
                    title={localization.toLanguageString(statusKey, mainMessages[siteLanguageDefault][statusKey])}
                    columnMenu={ColumnMenu}
                    cell={(props) => (
                        <td>
                            <span style={{
                                color: props.dataItem.status === 'ACTIVE' ? 'var(--color-success, #2e7d32)' : 'var(--color-text-secondary, #757575)',
                                fontWeight: 500
                            }}>
                                {props.dataItem.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                            </span>
                        </td>
                    )}
                />
            </Grid>
            {isVisible.addUser && <AddUserModal
                getUsers={getUsers}
                setIsVisible={setIsVisible}
                setIsLoading={setIsLoading}
            />}
            {isVisible.editUser && user && <EditUserModal
                user={user}
                setUser={setUser}
                getUsers={getUsers}
                setIsVisible={setIsVisible}
                setIsLoading={setIsLoading}
            />}
            {isVisible.removeUser && user && <RemoveUserModal
                user={user}
                setUser={setUser}
                getUsers={getUsers}
                setIsVisible={setIsVisible}
                setIsLoading={setIsLoading}
            />}
        </>
    );
}

export default UsersGrid;
